// src/components/ui/TagPicker.jsx - Popover to select/add tags
import React, { useState, useMemo } from 'react';
import { Plus, Check, Tag, Search } from 'lucide-react';
import { Button } from './button';
import { Input } from './input';
import { Popover, PopoverContent, PopoverTrigger } from './popover';
import { ScrollArea } from './scroll-area';
import { cn } from '../../utils/cn';
import { useTags } from '../../hooks';
import TagPill from './TagPill';

/**
 * TagPicker - Popover component for adding/removing tags from an entity
 *
 * @param {array} selectedTags - Currently applied tags (tagging objects with .tag property)
 * @param {function} onAddTag - Callback when tag is added (tagName, color)
 * @param {function} onRemoveTag - Callback when tag is removed (tagId)
 * @param {string} triggerClassName - Additional classes for trigger button
 * @param {string} size - 'sm' or 'md' for trigger button size
 * @param {boolean} showLabel - Show "Add tag" text on trigger
 */
const TagPicker = ({
  selectedTags = [],
  onAddTag,
  onRemoveTag,
  triggerClassName,
  size = 'sm',
  showLabel = false
}) => {
  const { tags: allTags, tagsLoading, TAG_COLORS } = useTags();
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  // Get IDs of currently selected tags
  const selectedTagIds = useMemo(() => {
    return new Set(selectedTags.map(t => (t.tag?.id || t.tag_id || t.id)));
  }, [selectedTags]);

  // Filter tags by search query
  const filteredTags = useMemo(() => {
    if (!searchQuery.trim()) return allTags;
    const query = searchQuery.toLowerCase();
    return allTags.filter(tag =>
      tag.name.toLowerCase().includes(query)
    );
  }, [allTags, searchQuery]);

  // Check if search query matches an existing tag
  const exactMatch = useMemo(() => {
    if (!searchQuery.trim()) return true;
    return allTags.some(tag =>
      tag.name.toLowerCase() === searchQuery.toLowerCase()
    );
  }, [allTags, searchQuery]);

  const handleTagClick = (tag) => {
    if (selectedTagIds.has(tag.id)) {
      onRemoveTag?.(tag.id);
    } else {
      onAddTag?.(tag.name, tag.color);
    }
  };

  const handleCreateTag = () => {
    if (!searchQuery.trim()) return;
    // Pick a random color from the palette
    const randomColor = TAG_COLORS[Math.floor(Math.random() * TAG_COLORS.length)];
    onAddTag?.(searchQuery.trim(), randomColor);
    setSearchQuery('');
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size={size}
          className={cn(
            "h-6 gap-1 text-muted-foreground hover:text-foreground",
            size === 'sm' && "px-2 text-xs",
            triggerClassName
          )}
        >
          <Plus className="w-3 h-3" />
          {showLabel && <span>Add tag</span>}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-64 p-0"
        align="start"
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        {/* Search Input */}
        <div className="p-2 border-b border-border">
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
            <Input
              placeholder="Search or create tag..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  if (filteredTags.length > 0 && !selectedTagIds.has(filteredTags[0].id)) {
                    handleTagClick(filteredTags[0]);
                  } else {
                    handleCreateTag();
                  }
                }
              }}
              className="h-8 pl-8 text-sm"
              autoFocus
            />
          </div>
        </div>

        {/* Tags List */}
        <ScrollArea className="max-h-48">
          <div className="p-1">
            {tagsLoading ? (
              <div className="py-4 text-center text-sm text-muted-foreground">
                Loading tags...
              </div>
            ) : filteredTags.length === 0 && !searchQuery.trim() ? (
              <div className="py-4 text-center text-sm text-muted-foreground">
                <Tag className="w-8 h-8 mx-auto mb-2 opacity-50" />
                No tags yet
              </div>
            ) : (
              <>
                {filteredTags.map((tag) => {
                  const isSelected = selectedTagIds.has(tag.id);
                  return (
                    <button
                      key={tag.id}
                      onClick={() => handleTagClick(tag)}
                      className={cn(
                        "w-full flex items-center gap-2 px-2 py-1.5 rounded-md text-sm text-left transition-colors",
                        isSelected
                          ? "bg-primary/10"
                          : "hover:bg-muted"
                      )}
                    >
                      <div
                        className="w-3 h-3 rounded-full flex-shrink-0"
                        style={{ backgroundColor: tag.color || '#6366F1' }}
                      />
                      <span className="flex-1 truncate">{tag.name}</span>
                      {isSelected && (
                        <Check className="w-4 h-4 text-primary flex-shrink-0" />
                      )}
                    </button>
                  );
                })}

                {/* Create new tag option */}
                {searchQuery.trim() && !exactMatch && (
                  <button
                    onClick={handleCreateTag}
                    className="w-full flex items-center gap-2 px-2 py-1.5 rounded-md text-sm text-left hover:bg-muted text-primary"
                  >
                    <Plus className="w-3 h-3 flex-shrink-0" />
                    <span>Create "{searchQuery.trim()}"</span>
                  </button>
                )}
              </>
            )}
          </div>
        </ScrollArea>

        {/* Footer hint */}
        <div className="p-2 border-t border-border">
          <p className="text-[10px] text-muted-foreground text-center">
            Click to toggle • Type to search or create
          </p>
        </div>
      </PopoverContent>
    </Popover>
  );
};

/**
 * TagManager - Combined display of tags with picker
 *
 * Shows existing tags as pills with remove buttons, plus an add button
 *
 * @param {array} tags - Currently applied tags (tagging objects)
 * @param {function} onAddTag - Callback when tag is added
 * @param {function} onRemoveTag - Callback when tag is removed
 * @param {string} size - 'sm' or 'md'
 * @param {string} className - Additional classes
 */
export const TagManager = ({
  tags = [],
  onAddTag,
  onRemoveTag,
  size = 'sm',
  className
}) => {
  // Extract tag data from tagging objects
  const tagData = tags.map(t => t.tag || t);

  const handleRemove = (tag) => {
    onRemoveTag?.(tag.id);
  };

  return (
    <div className={cn("flex flex-wrap items-center gap-1", className)}>
      {tagData.map((tag) => (
        <TagPill
          key={tag.id}
          tag={tag}
          size={size}
          removable
          onRemove={handleRemove}
        />
      ))}
      <TagPicker
        selectedTags={tags}
        onAddTag={onAddTag}
        onRemoveTag={onRemoveTag}
        size={size}
        showLabel={tags.length === 0}
      />
    </div>
  );
};

export default TagPicker;
